import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Page, alert } from 'equasion-lib';
import { FadeLoader } from 'react-spinners';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';
import SharedScanner from '@/lib/shared-coms/SharedScanner';
import { SCAN_PROFILE, useScanner } from '@/root/App/com/ScannerProvider/ScannerProvider';
import { playFailTone, playInfoTone, playSuccessTone } from '@/lib/scanning/feedback';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import ReceiveLineRow from './com/ReceiveLineRow';
import ReceiveManualEntry from './com/ReceiveManualEntry';
import { eqProps } from './eqProps';
import {
  classifyReceiveScan,
  createReceiveScanQueue,
  initialReceiveGateState,
  orderReceiveRows,
  receiveOutcomeFeedback,
  type ReceiveLine,
  type ReceiveList,
  type ReceiveScanJob,
  type ReceiveScanLike,
  type ReceiveScanResult
} from './receiveScan';
import './PurchaseOrderReceive.scss';

const REARM_DELAY_MS = 250;

type PurchaseOrderReceiveProps = {
  list: ReceiveList;
  onScan: (job: ReceiveScanJob) => Promise<ReceiveScanResult>;
  onReset: (productId: string) => Promise<void>;
  onComplete: () => Promise<{ receivedUnitCount: number; purchaseOrderNumber?: string | null }>;
};

type Completed = { receivedUnitCount: number; purchaseOrderNumber?: string | null };

/**
 * Standard warehouse Receive. The scanner stays open; the PO's products sit underneath it.
 * Each case, inner pack or each checks off its product. Complete marks every remaining unit
 * received, and stocking is the same scan-to-stock flow as before.
 */
function PurchaseOrderReceive({ list, onScan, onReset, onComplete }: PurchaseOrderReceiveProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { emitDecodeFeedback } = useScanner() || {};
  const [manualOpen, setManualOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [completed, setCompleted] = useState<Completed | null>(null);
  const [flash, setFlash] = useState<{ key: string; n: number }>({ key: '', n: 0 });

  const gateRef = useRef(initialReceiveGateState());
  const inFlightRef = useRef(new Set<string>());
  const manualOpenRef = useRef(false);
  const pausedRef = useRef(false);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  manualOpenRef.current = manualOpen;
  pausedRef.current = !!completed || busy;

  const applyFeedback = (result: ReceiveScanResult, job: ReceiveScanJob) => {
    const feedback = receiveOutcomeFeedback(result, { opening: job.opening });
    if (feedback.kind === 'silent') return;
    if (feedback.kind === 'success') {
      playSuccessTone();
      if (job.manualEntry) emitDecodeFeedback?.();
      setFlash((current) => ({
        key: feedback.productId,
        n: current.key === feedback.productId ? current.n + 1 : 1
      }));
      return;
    }
    if (feedback.tone === 'info') playInfoTone();
    else playFailTone();
    alert(feedback.title, feedback.text, 6, feedback.tone === 'info' ? 'info' : 'error');
  };

  const queueRef = useRef(
    createReceiveScanQueue(async (job) => {
      try {
        const result = await onScanRef.current(job);
        applyFeedback(result, job);
      } catch (error) {
        playFailTone();
        alert(
          'Could not check that scan',
          operatorFacingMessage(error, 'Check the connection and scan it again.'),
          6,
          'error'
        );
      } finally {
        inFlightRef.current.delete(job.key);
      }
    })
  );

  const handleScan = (scan: ReceiveScanLike) => {
    const classified = classifyReceiveScan(gateRef.current, scan, {
      manualOpen: manualOpenRef.current,
      paused: pausedRef.current,
      inFlight: inFlightRef.current,
      now: Date.now()
    });
    gateRef.current = classified.state;
    const decision = classified.decision;
    if (decision.type === 'hint') {
      playFailTone();
      alert(decision.title, decision.message, 6, 'warning');
      return;
    }
    if (decision.type !== 'enqueue') return;
    inFlightRef.current.add(decision.job.key);
    queueRef.current.enqueue(decision.job);
  };
  const handleScanRef = useRef(handleScan);
  handleScanRef.current = handleScan;

  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    const serialNumber = searchParams.get('serialNumber');
    if (!serialNumber) return;
    handleScanRef.current({
      serialNumber,
      gtin: searchParams.get('gtin'),
      text: serialNumber,
      source: 'camera',
      opening: true
    });
  }, [searchParams]);

  const undo = async (line: ReceiveLine) => {
    try {
      await onReset(line.productId);
    } catch (error) {
      alert('Could not undo', operatorFacingMessage(error, 'Try again.'), 6, 'error');
    }
  };

  const complete = async () => {
    if (!list.canComplete || busy || completed) return;
    setBusy(true);
    try {
      setCompleted(await onComplete());
    } catch (error) {
      alert(
        'Could not complete receive',
        operatorFacingMessage(error, 'Try again.'),
        6,
        'error'
      );
    } finally {
      setBusy(false);
    }
  };

  const rows = orderReceiveRows(list.lines || []);
  const total = rows.length;
  const confirmed = list.confirmedCount;
  const poNumber = list.clientPurchaseOrderNumber;
  const flashFor = (productId: string) =>
    flash.key === productId ? (flash.n % 2 ? 'a' : 'b') : null;

  return (
    <Page className="purchase-order-receive action-page">
      <div className="action-top-bar">
        <div className="order-details">
          <h1>Receive — PO #{poNumber}</h1>
          <h2>
            {confirmed} of {total} products confirmed
            {list.vendorName ? ` - ${list.vendorName}` : ''}
          </h2>
        </div>
        <FiXCircle aria-label="Close" onClick={() => navigate('/')} />
      </div>

      <div className="action-page-body">
        <div className="receive-camera">
          <SharedScanner
            showSSCC
            scanProfile={SCAN_PROFILE.HOME}
            silentDecode
            rearmDelayMs={REARM_DELAY_MS}
            replaceManualEntry
            alternativeManualEntry={
              <ReceiveManualEntry
                onSubmit={(unit) =>
                  handleScan({ ...unit, text: unit.serialNumber, isManualEntry: true })
                }
              />
            }
            onEntryOpenChange={setManualOpen}
            handleScan={handleScan}
          />
        </div>

        <div className="receive-summary">
          <div className="receive-summary__count">
            <span className="receive-summary__number">{confirmed}</span>
            <span className="receive-summary__of">/ {total}</span>
          </div>
          <div className="receive-summary__label">
            <span className="receive-summary__heading">
              Products confirmed
              {list.openCount > 0 && (
                <span className="receive-summary__togo">{list.openCount} to scan</span>
              )}
            </span>
            {list.notInShipmentCount > 0 && (
              <small>
                {list.notInShipmentCount} not in shipment
              </small>
            )}
          </div>
        </div>

        <div className="receive-lines">
          {rows.map((line) => (
            <ReceiveLineRow
              key={line.productId}
              line={line}
              flash={flashFor(line.productId)}
              onUndo={undo}
            />
          ))}
        </div>

        <div className="purchase-order-receive__footer">
          <Button
            type="primary"
            fullWidth
            disabled={!list.canComplete || busy || !!completed}
            {...eqProps({ onClick: complete })}
          >
            Complete Receive
          </Button>
          {!list.canComplete && list.completeBlockedReason && (
            <p className="purchase-order-receive__blocked">{list.completeBlockedReason}</p>
          )}
        </div>
      </div>

      <div className={['result-panel', completed && 'open'].filter(Boolean).join(' ')}>
        {completed && (
          <>
            <div className="result-message">
              <FiCheckCircle style={{ color: 'var(--green)' }} />
              <h1>Receive complete</h1>
              <p>
                {completed.receivedUnitCount} units received on PO #{completed.purchaseOrderNumber || poNumber}.
                Ready to stock.
              </p>
            </div>
            <div className="result-actions">
              <Button type="primary" fullWidth {...eqProps({ onClick: () => navigate('/') })}>
                Done
              </Button>
            </div>
          </>
        )}
      </div>

      {busy && (
        <div className="blocking-spinner-container" onClick={(event) => event.stopPropagation()}>
          <FadeLoader color="var(--n0)" />
        </div>
      )}
    </Page>
  );
}

export default PurchaseOrderReceive;
